import React, { useCallback, useEffect, useState } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Button, Form, FormGroup, Input, Label, Table } from "reactstrap";
import Loading from "../components/Loading";
import OrgHeader from "../components/OrgHeader";
import { getOrganisation, readSelfProfile } from "../utils/apiService";
import { roleFor } from "../utils/orgRoles";
import {
  ACTIVATION_STATE,
  DEFAULT_VISIT_RADIUS_METERS,
  hasLocation,
  readOfferLinks,
  readOrgOffers,
  readPlatformServices,
  readVisitRule,
  saveOffer,
  saveVisitRule,
  setPublished,
} from "../utils/offersApi";

/** The homepage categories: platform service ids come in blocks of 100 per category (api/WidebySeed.cs). */
const CATEGORY_NAMES = {
  100: "Home & Garden",
  200: "Wellness & Beauty",
  300: "Automotive",
  400: "Food & Drink",
  500: "Fitness",
  600: "Repairs & Maintenance",
};
export const categoryOf = (serviceId) => CATEGORY_NAMES[Math.floor(serviceId / 100) * 100] || "Other";

/** A new offer: no visit rule (available to everyone nearby) until the business asks for one. */
const EMPTY_RULE = {
  requireVisits: false,
  hasRule: false,
  minVisits: "5",
  windowDays: "30",
  maxDistanceMeters: String(DEFAULT_VISIT_RADIUS_METERS),
  distinctDaysOnly: true,
};

const EMPTY_FORM = { serviceFulfilmentConfigId: 0, serviceId: "", name: "", price: "", durationMinutes: "30", isActive: ACTIVATION_STATE.INACTIVE, ...EMPTY_RULE };

export const OrganisationOffersComponent = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [org, setOrg] = useState(null);
  const [services, setServices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(null);

  const token = useCallback(() => getAccessTokenSilently().catch(() => null), [getAccessTokenSilently]);

  const load = useCallback(async () => {
    setError("");
    try {
      const t = await token();
      const [data, platformServices, orgOffers, self] = await Promise.all([
        getOrganisation(id, t),
        readPlatformServices(t),
        readOrgOffers(id, t),
        readSelfProfile(t),
      ]);
      setOrg({
        orgId: Number(id),
        name: data?.name || data?.orgName || data?.organisationName || "Unknown",
        identifier: data?.identifier || data?.orgIdentifier || "",
        role: roleFor(data, self?.userId),
        latitude: data?.latitude || 0,
        longitude: data?.longitude || 0,
        monetaryCurrency: data?.monetaryCurrency,
      });
      setServices(platformServices.sort((a, b) => a.serviceId - b.serviceId));
      setOffers(orgOffers);
    } catch (err) {
      setError(err?.message || "Failed to load offers");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    load();
  }, [load]);

  const serviceName = (serviceId) => services.find((s) => s.serviceId === serviceId)?.name || `Service ${serviceId}`;

  const run = async (key, action, done) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await action(await token());
      setNotice(done);
      await load();
      return true;
    } catch (err) {
      setError(err?.message || "Something went wrong");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const edit = async (offer) => {
    setError("");
    try {
      const t = await token();
      const [links, rule] = await Promise.all([
        readOfferLinks(org.orgId, offer.serviceFulfilmentConfigId, t),
        readVisitRule(org.orgId, offer.serviceFulfilmentConfigId, t),
      ]);
      setForm({
        serviceFulfilmentConfigId: offer.serviceFulfilmentConfigId,
        serviceId: String(offer.serviceId),
        name: offer.name,
        price: String(offer.monetaryAmount),
        durationMinutes: String(links[0]?.durationMinutes || 30),
        isActive: offer.isActive,
        ...(rule
          ? {
              requireVisits: true,
              hasRule: true,
              minVisits: String(rule.minVisits),
              windowDays: String(rule.windowDays),
              maxDistanceMeters: String(rule.maxDistanceMeters ?? DEFAULT_VISIT_RADIUS_METERS),
              distinctDaysOnly: rule.distinctDaysOnly,
            }
          : EMPTY_RULE),
      });
    } catch (err) {
      setError(err?.message || "Failed to load the offer");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.serviceId || !form.name.trim()) {
      setError("Choose a service and give the offer a name.");
      return;
    }
    const isNew = !form.serviceFulfilmentConfigId;
    const saved = await run("save",
      async (t) => {
        const offerId = await saveOffer(org, { ...form, serviceId: Number(form.serviceId), name: form.name.trim() }, t);
        if (form.requireVisits) await saveVisitRule(org.orgId, offerId, form, t);
      },
      isNew ? "Offer saved as a draft. Publish it to show it on the deals map." : "Offer saved.");
    if (saved) setForm(EMPTY_FORM);
  };

  const togglePublished = (offer, published) =>
    run(`publish-${offer.serviceFulfilmentConfigId}`, (t) => setPublished(org, offer, published, t),
      published ? `"${offer.name}" is published and shows on the deals map.` : `"${offer.name}" is no longer shown on the map.`);

  if (loading) return <Loading />;

  const located = org && hasLocation(org);
  const groupedServices = services.reduce((groups, s) => {
    const name = categoryOf(s.serviceId);
    (groups[name] = groups[name] || []).push(s);
    return groups;
  }, {});

  return (
    <div style={{ paddingBottom: "3rem" }}>
      <OrgHeader id={id} name={org?.name} role={org?.role} identifier={org?.identifier} />
      <p className="tab-intro">Deals people nearby can unlock by visiting{org ? ` ${org.name}` : ""}.</p>

      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {org && !located && (
        <Alert color="warning">
          Add your business location first: customers find offers by where you are.{" "}
          <a href="#edit" onClick={(e) => { e.preventDefault(); history.push(`/organisations/${id}/edit`); }}>Set location</a>
        </Alert>
      )}

      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">{form.serviceFulfilmentConfigId ? "Edit offer" : "New offer"}</div>
        </div>
        <div className="panel-body">
          <Form onSubmit={save}>
            <fieldset disabled={!located || busy !== null}>
              <FormGroup>
                <Label for="offerService">Service</Label>
                <Input id="offerService" type="select" value={form.serviceId}
                  onChange={(e) => setForm({ ...form, serviceId: e.target.value })}>
                  <option value="">Choose what you offer…</option>
                  {Object.entries(groupedServices).map(([category, list]) => (
                    <optgroup key={category} label={category}>
                      {list.map((s) => <option key={s.serviceId} value={s.serviceId}>{s.name}</option>)}
                    </optgroup>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label for="offerName">Offer name</Label>
                <Input id="offerName" value={form.name} placeholder="e.g. Lunch deal, Haircut and wash"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormGroup>
              <div className="d-flex">
                <FormGroup className="mr-3">
                  <Label for="offerPrice">Price (SEK)</Label>
                  <Input id="offerPrice" type="number" min="0" step="1" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label for="offerDuration">Length (minutes)</Label>
                  <Input id="offerDuration" type="number" min="5" step="5" value={form.durationMinutes}
                    onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
                </FormGroup>
              </div>
              <FormGroup check className="mb-2">
                <Label check>
                  <Input type="checkbox" checked={form.requireVisits} disabled={form.hasRule}
                    onChange={(e) => setForm({ ...form, requireVisits: e.target.checked })} />{" "}
                  Visitors unlock this offer by visiting you
                </Label>
                <small className="form-text text-muted">
                  {form.hasRule
                    ? "This offer has a visit rule. You can change it here; it can't be removed yet."
                    : "Without this, the offer is shown to everyone nearby. With it, customers using the Wideby app see it only after visiting you often enough."}
                </small>
              </FormGroup>
              {form.requireVisits && (
                <div className="border rounded p-3 mb-3">
                  <div className="d-flex flex-wrap">
                    <FormGroup className="mr-3">
                      <Label for="ruleMinVisits">Visits needed</Label>
                      <Input id="ruleMinVisits" type="number" min="1" max="1000" step="1" value={form.minVisits}
                        onChange={(e) => setForm({ ...form, minVisits: e.target.value })} />
                    </FormGroup>
                    <FormGroup className="mr-3">
                      <Label for="ruleWindowDays">Within (days)</Label>
                      <Input id="ruleWindowDays" type="number" min="1" max="365" step="1" value={form.windowDays}
                        onChange={(e) => setForm({ ...form, windowDays: e.target.value })} />
                    </FormGroup>
                    <FormGroup>
                      <Label for="ruleRadius">Counts within (metres)</Label>
                      <Input id="ruleRadius" type="number" min="1" max="99999" step="50" value={form.maxDistanceMeters}
                        onChange={(e) => setForm({ ...form, maxDistanceMeters: e.target.value })} />
                    </FormGroup>
                  </div>
                  <FormGroup check>
                    <Label check>
                      <Input type="checkbox" checked={form.distinctDaysOnly}
                        onChange={(e) => setForm({ ...form, distinctDaysOnly: e.target.checked })} />{" "}
                      Count at most one visit per day
                    </Label>
                  </FormGroup>
                </div>
              )}
              <Button color="primary" type="submit">
                {busy === "save" ? "Saving…" : form.serviceFulfilmentConfigId ? "Save offer" : "Create offer"}
              </Button>{" "}
              {form.serviceFulfilmentConfigId !== 0 && (
                <Button color="link" type="button" onClick={() => setForm(EMPTY_FORM)}>Cancel</Button>
              )}
            </fieldset>
          </Form>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">Your offers{offers.length ? ` (${offers.length})` : ""}</div>
        </div>
          {offers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏷️</div>
              <div className="empty-title">No offers yet</div>
              <div className="empty-text">Create one above, then publish it to appear on the Wideby deals map.</div>
            </div>
          ) : (
            <Table responsive className="data-table">
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>Service</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => {
                  const live = o.isActive === ACTIVATION_STATE.ACTIVE;
                  const key = `publish-${o.serviceFulfilmentConfigId}`;
                  return (
                    <tr key={o.serviceFulfilmentConfigId}>
                      <td className="cell-strong">{o.name}</td>
                      <td className="cell-muted">{serviceName(o.serviceId)} <small className="text-muted">({categoryOf(o.serviceId)})</small></td>
                      <td className="cell-muted">{o.monetaryAmount} SEK</td>
                      <td>{live ? <span className="tag btn-pill-green">Published</span> : <span className="tag tag-grey">Draft</span>}</td>
                      <td>
                        <div className="row-actions">
                          <button className="btn-pill btn-pill-grey" disabled={busy !== null} onClick={() => edit(o)}>Edit</button>
                          {live ? (
                            <button className="btn-pill btn-pill-grey" disabled={busy !== null} onClick={() => togglePublished(o, false)}>
                              {busy === key ? "…" : "Unpublish"}
                            </button>
                          ) : (
                            <button className="btn-pill btn-pill-green" disabled={busy !== null || !located} onClick={() => togglePublished(o, true)}>
                              {busy === key ? "Publishing…" : "Publish"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
      </div>
    </div>
  );
};

export default withAuthenticationRequired(OrganisationOffersComponent, {
  onRedirecting: () => <Loading />,
});
